/**
    Used to hold state regarding things that google tag manager can read. In the thorne site we refer to this as 'gtmDataLayer'.
    We fill this object up in the checkout screen, thank you page (after purchase) and on product details.
    todo: We don't have any of these pages yet - once we do we'll need to set this stuff when the page loads and
        then get the data into gtm (see components/app/header)/
 */
import type {CartLine} from "~/stores/cartStore";
import type { AddToCartAnalyticsContext } from "~~/shared/types/common";
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useGtmStore = defineStore('gtmDataLayer', () => {
    const userStore = useUserStore();
    const attributes = ref<object>({});

    function hasAttributes(): boolean {
        return Object.keys(attributes.value).length > 0;
    }

    function setAttribute(key: any, value: any): void {
        attributes.value[key] = value;
    }

    function clear(): void {
        for (const key in attributes.value) {
            delete attributes.value[key]
        }
    }

    function pushEvent(event) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(event);
    }

    function pushAttributes() {
        if (hasAttributes()) {
            window.dataLayer = window.dataLayer || [];
            for (const [key, value] of Object.entries(attributes.value)) {
                window.dataLayer.push({ key, value })
            }
        }
    }

    function pushVisitorInformation() {
        if (!userStore.loggedIn) return;

        pushEvent({
            visitor: {
                customerId: userStore.data?.customerId,
                timeZone: userStore.data?.timeZone,
                locale: userStore.data?.locale,
                priceGroup: userStore.data?.priceGroup,
                isAffiliateClient: userStore.data?.isAffiliateClient
            }
        });
    }

    function addToCartEvent(line: CartLine) {
        pushEvent({
            event: 'addToCart',
            productSku: line.sku,
            productName: line.name,
            productPrice: line.subtotal.replace(/[^\d.-]/g, ""),
            quantity: line.quantity,
            currencyCode: 'USD'
        })
    }

    function agenticEcommerceEvent(event: string, context?: AddToCartAnalyticsContext | null, quantity: number = 1) {
        if (!context) return null;

        pushEvent({ ecommerce: null });
        pushEvent({
            event,
            screen_name: context.screen_name ?? 'Agenticplp_results',
            sub_screen: context.sub_screen ?? 'Product_summary',
            Customer_id: userStore.data?.customerId ?? null,
            ecommerce: {
                currency: 'USD',
                value: context.price,
                items: [
                    {
                        item_id: context.item_id ?? null,
                        item_name: context.item_name ?? null,
                        item_category: context.item_category ?? null,
                        price: context.price,
                        quantity,
                        item_list_id: context.item_list_id ?? 'agentic_plp_results',
                        item_list_name: context.item_list_name ?? 'Agentic PLP Results'
                    }
                ]
            }
        });
    }

    function selectProductEvent(context?: AddToCartAnalyticsContext | null, quantity: number = 1) {
        return agenticEcommerceEvent('select_product', context, quantity);
    }

    function productViewedEvent(context?: AddToCartAnalyticsContext | null, quantity: number = 1) {
        return agenticEcommerceEvent('product_viewed', context, quantity);
    }


    function pageViewEvent(path: string, title?: string) {
        pushEvent({
            event: 'pageview',
            title,
            path
        });

        console.debug('GTM pageview logged for:', title, path);
    }

    return { attributes, hasAttributes, setAttribute, clear, pushEvent, pushAttributes, pushVisitorInformation, addToCartEvent, selectProductEvent, productViewedEvent, pageViewEvent }
})
